/**
 * The salary PIN (ADR 0041): who is asked for one, and what a PIN may be.
 *
 * The trigger is a PERMISSION fact, never a role name — roles are customisable per organisation
 * (ADR 0023), so "HR Manager needs a PIN" would be wrong the day a company builds its own payroll
 * role. These permissions each grant a view of someone ELSE's pay; their `.own` counterparts
 * (one's own payslip, one's own CTC) never lead to a PIN.
 *
 * Shared by the API (which refuses a gated response without a fresh unlock) and the web (which shows
 * the Salary PIN settings only to people who will ever be asked). Browser-safe: no Node built-ins.
 */

import { canAnywhere, type Actor } from './can.js';
import type { Permission } from './permissions.js';

/**
 * Holding any one of these at any scope means the caller can see other people's pay somewhere.
 *
 * The first five are HR's list. The last four are the other doors to a pay-bearing route: a Reporting
 * Manager's merit worksheet (`merit:read`: the team's current and proposed CTC), settlements (`fnf:read`),
 * salary payments (`payroll.payment:read`: net pay per person) and the leave year-end run
 * (`leave.year_end:read`: encashment priced at basic/30). Without them a role holding only those would
 * read others' pay with no PIN (ADR 0041, "Who is asked"). Every route behind any of these is either
 * tagged or on `salary-pin.reviewed.ts` (`contracts/salary-pin-meta.test.ts`).
 */
export const SALARY_PIN_PERMISSIONS = [
  'compensation:read',
  'payslip:read',
  'payroll:read',
  'analytics.compensation:read',
  'offer.compensation:read',
  'merit:read',
  'fnf:read',
  'payroll.payment:read',
  'leave.year_end:read',
] as const satisfies readonly Permission[];

/**
 * Audited tables whose trigger row images (`audit_log.before` / `after`) carry someone's pay. The audit
 * viewers drop those images unless the caller may see pay right now (ADR 0041), and keep the rest of the
 * row: who changed which record, and when, stays readable without the PIN.
 */
export const SALARY_PIN_AUDIT_ENTITY_TYPES = [
  'compensations',
  'fnf_calculation_versions',
  'fnf_settlements',
  'leave_encashments',
  'leave_year_end_runs',
  'merit_compensation_proposals',
  'merit_recommendations',
  'payroll_calculation_versions',
  'payroll_employee_attributes',
  'payroll_form16',
  'payroll_gl_postings',
  'payroll_off_cycle_payments',
  'payroll_opening_balances',
  'payroll_payment_batches',
  'payroll_payment_obligations',
  'payroll_profiles',
  'payroll_result_outputs',
  'payroll_run_finalizations',
  'payroll_run_input_snapshots',
  'payroll_runs',
  'payroll_salary_holds',
  'payroll_salary_queries',
  'payroll_statutory_filings',
  'payroll_variable_inputs',
] as const;

const PAY_AUDIT_ENTITY_TYPES: ReadonlySet<string> = new Set(SALARY_PIN_AUDIT_ENTITY_TYPES);

/** True when this audited table's row images carry someone's pay. */
export function isSalaryAuditEntityType(entityType: string): boolean {
  return PAY_AUDIT_ENTITY_TYPES.has(entityType);
}

export const SALARY_PIN_MIN_LENGTH = 4;
export const SALARY_PIN_MAX_LENGTH = 6;
/** How long a correct PIN keeps pay unlocked for this login session. Absolute, never sliding. */
export const SALARY_PIN_UNLOCK_SECONDS = 300;
/** Wrong PINs in a row before the PIN locks. */
export const SALARY_PIN_MAX_ATTEMPTS = 5;
/** How long the PIN stays locked after `SALARY_PIN_MAX_ATTEMPTS` wrong entries. */
export const SALARY_PIN_LOCK_SECONDS = 300;

/** True when this caller can see someone else's pay somewhere, and so must unlock it with a PIN. */
export function needsSalaryPin(actor: Actor): boolean {
  return SALARY_PIN_PERMISSIONS.some((p) => canAnywhere(actor, p));
}

/** Refused: all one digit (1111), or a straight run up or down (1234, 6543). */
export function isWeakSalaryPin(pin: string): boolean {
  if (!/^\d+$/.test(pin) || pin.length < 2) return false;
  const digits = [...pin].map(Number);
  const step = digits[1]! - digits[0]!;
  if (step !== 0 && step !== 1 && step !== -1) return false;
  return digits.every((d, i) => i === 0 || d - digits[i - 1]! === step);
}
